import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Track if we're already redirecting to avoid multiple redirects
let isRedirecting = false;

// Add response interceptor to handle 401 errors gracefully
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only handle 401 errors for session expiration
    if (error.response?.status === 401 && !isRedirecting) {
      const requestUrl = error.config?.url || '';
      
      // List of endpoints that should NOT trigger a logout
      const authEndpoints = [
        '/auth/login',
        '/auth/register',
        '/auth/users-for-login',
        '/auth/login-pin'
      ];
      
      const isAuthRequest = authEndpoints.some(endpoint => requestUrl.includes(endpoint));
      const isOnAuthPage = window.location.pathname.includes('/login') || window.location.pathname === '/';
      
      // Only redirect if:
      // 1. Not an auth request
      // 2. Not already on login/selector page
      // 3. The error message indicates token issue (not just permission denied)
      const errorDetail = error.response?.data?.detail || '';
      const isTokenError = errorDetail.toLowerCase().includes('token') || 
                          errorDetail.toLowerCase().includes('expired') ||
                          errorDetail.toLowerCase().includes('invalid') ||
                          errorDetail === 'Not authenticated';
      
      if (!isAuthRequest && !isOnAuthPage && isTokenError) {
        console.warn('Session expired, redirecting to login...', errorDetail);
        isRedirecting = true;
        
        // Clear session data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('businesses');
        localStorage.removeItem('selectedBusinessId');
        delete axios.defaults.headers.common['Authorization'];
        delete axios.defaults.headers.common['X-Business-Id'];
        
        // Redirect to business selector
        setTimeout(() => {
          isRedirecting = false;
          window.location.href = '/';
        }, 100);
      }
    }
    return Promise.reject(error);
  }
);

// Generic API helper using global axios (headers are set by AuthContext)
const api = {
  get: (endpoint) => axios.get(`${API_URL}${endpoint}`),
  post: (endpoint, data) => axios.post(`${API_URL}${endpoint}`, data),
  put: (endpoint, data) => axios.put(`${API_URL}${endpoint}`, data),
  patch: (endpoint, data) => axios.patch(`${API_URL}${endpoint}`, data),
  delete: (endpoint) => axios.delete(`${API_URL}${endpoint}`),
};

// Build a query string from params, skipping null/undefined/'' values.
// Avoids serializing undefined as the literal string "undefined" (which breaks FastAPI bool/int parsing).
const toQuery = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    usp.append(k, v);
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
};

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  loginWithPin: (userId, pinCode) => api.post('/auth/login-pin', { user_id: userId, pin_code: pinCode }),
  getUsersForLogin: () => axios.get(`${API_URL}/auth/users-for-login`),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Businesses
export const businessAPI = {
  getAll: () => api.get('/businesses'),
  get: (id) => api.get(`/businesses/${id}`),
  update: (id, data) => api.put(`/businesses/${id}`, data),
};

// Users
export const usersAPI = {
  getAll: (role) => api.get(`/users${role ? `?role=${role}` : ''}`),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  hardDelete: (id) => api.delete(`/users/${id}?hard=true`),
};

// Barbers
export const barbersAPI = {
  getAll: (params = {}) => api.get(`/barbers${toQuery(params)}`),
  get: (id) => api.get(`/barbers/${id}`),
  create: (data) => api.post('/barbers', data),
  update: (id, data) => api.put(`/barbers/${id}`, data),
  delete: (id) => api.delete(`/barbers/${id}`),
  // Dedupe endpoint - allows overriding X-Business-Id to run against a specific business
  // (needed when the admin wants to clean up multiple businesses from a single screen).
  deduplicate: (businessId) =>
    axios.post(`${API_URL}/barbers/deduplicate`, null, {
      headers: businessId ? { 'X-Business-Id': businessId } : {},
    }),
};

// Clients
export const clientsAPI = {
  getAll: (params = {}) => api.get(`/clients${toQuery(params)}`),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getHistory: (id) => api.get(`/clients/${id}/history`),
  getAppointmentHistory: (id, params = {}) => api.get(`/clients/${id}/appointment-history${toQuery(params)}`),
};

// Services
export const servicesAPI = {
  getAll: (params = {}) => api.get(`/services${toQuery(params)}`),
  get: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

// Service categories (persisted per business)
export const serviceCategoriesAPI = {
  list: () => api.get('/service-categories'),
  create: (name) => api.post('/service-categories', { name }),
  delete: (id) => api.delete(`/service-categories/${id}`),
};

// Products
export const productsAPI = {
  getAll: (params = {}) => api.get(`/products${toQuery(params)}`),
  get: (id) => api.get(`/products/${id}`),
  getByBarcode: (barcode) => api.get(`/products/barcode/${barcode}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  deductContent: (id, amount) => api.put(`/products/${id}/deduct-content?amount=${amount}`),
};

// Inventory
export const inventoryAPI = {
  getMovements: (params = {}) => api.get(`/inventory/movements${toQuery(params)}`),
  createMovement: (data) => api.post('/inventory/movements', data),
  getAlerts: () => api.get('/inventory/alerts'),
};

// Appointments
export const appointmentsAPI = {
  getAll: (params = {}) => api.get(`/appointments${toQuery(params)}`),
  get: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  delete: (id) => api.delete(`/appointments/${id}`),
  history: (id) => api.get(`/appointments/${id}/history`),
};

// Sales
export const salesAPI = {
  getAll: (params = {}) => api.get(`/sales${toQuery(params)}`),
  get: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post('/sales', data),
  updatePaymentMethod: (id, payment_method, notes) =>
    api.patch(`/sales/${id}/payment-method`, { payment_method, notes }),
  getAudits: (id) => api.get(`/sales/${id}/audits`),
  requestTotalChange: (id, new_total, reason) =>
    api.post(`/sales/${id}/request-total-change`, { new_total, reason }),
  requestCancel: (id, reason) =>
    api.post(`/sales/${id}/request-cancel`, { reason }),
  getEditRequests: (status) => api.get(`/sale-edit-requests${status ? `?status=${status}` : ''}`),
  getPendingEditRequestsCount: () => api.get('/sale-edit-requests/pending-count'),
  approveEditRequest: (id, notes) => api.post(`/sale-edit-requests/${id}/approve`, { notes }),
  rejectEditRequest: (id, notes) => api.post(`/sale-edit-requests/${id}/reject`, { notes }),
};

// Expenses
export const expensesAPI = {
  getAll: (params = {}) => api.get(`/expenses${toQuery(params)}`),
  create: (data) => api.post('/expenses', data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

// Commissions
export const commissionsAPI = {
  getAll: (params = {}) => api.get(`/commissions${toQuery(params)}`),
  pay: (barberId, commissionIds) => api.post(`/commissions/pay?barber_id=${barberId}`, commissionIds),
  payEarnings: (barberId, startDate, endDate) =>
    api.post(`/barbers/${barberId}/pay-earnings?start_date=${startDate}&end_date=${endDate}`),
  payrollHistory: (barberId, limit = 50) =>
    api.get(`/barbers/${barberId}/payroll-history?limit=${limit}`),
};

// Cash Register
export const cashRegisterAPI = {
  getAll: () => api.get('/cash-registers'),
  get: (id) => api.get(`/cash-registers/${id}`),
  open: (data) => api.post('/cash-registers/open', data),
  close: (data) => api.post('/cash-registers/close', data),
  withdrawal: (data) => api.post('/cash-registers/withdrawal', data),
  deposit: (data) => api.post('/cash-registers/deposit', data),
  // NEW: list all movements (withdrawals/deposits/adjustments) in a range —
  // used by Reportes → Transacciones to unify sales + expenses + retiros.
  getMovements: (params = {}) => api.get(`/cash-registers/movements${toQuery(params)}`),
};

// Loyalty (stamp-card program, admin-managed)
export const loyaltyAPI = {
  listMembers: (search) => api.get(`/loyalty/members${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  enroll: (payload) => api.post('/loyalty/enroll', payload),
  unenroll: (clientId) => api.delete(`/loyalty/members/${clientId}`),
  manualStamp: (clientId) => api.post(`/loyalty/members/${clientId}/manual-stamp`),
  stampsLog: (clientId, limit = 50) => api.get(`/loyalty/members/${clientId}/stamps-log?limit=${limit}`),
  undoLastStamp: (clientId) => api.delete(`/loyalty/members/${clientId}/stamps/last`),
  getCards: (clientId) => api.get(`/loyalty/members/${clientId}/cards`),
  redeemReward: (cardId, reward_type, sale_id) =>
    api.post(`/loyalty/rewards/${cardId}/redeem`, { reward_type, sale_id }),
  publicCardAccess: (token, last4) =>
    api.post(`/public/loyalty/card/${token}`, { last4 }),
  publicReferralInfo: (token) => api.get(`/public/referral/${token}`),
  publicReferralSignup: (token, payload) => api.post(`/public/referral/${token}`, payload),
};

// Formulations (JL Saloon)
export const formulationsAPI = {
  getAll: (clientId) => api.get(`/formulations${clientId ? `?client_id=${clientId}` : ''}`),
  create: (data) => api.post('/formulations', data),
  update: (id, data) => api.put(`/formulations/${id}`, data),
  delete: (id) => api.delete(`/formulations/${id}`),
  apply: (id) => api.post(`/formulations/${id}/apply`),
  history: (id) => api.get(`/formulations/${id}/history`),
};

// Waitlist
export const waitlistAPI = {
  getAll: () => api.get('/waitlist'),
  add: (data) => api.post('/waitlist', data),
  updateStatus: (id, status) => api.put(`/waitlist/${id}/status?status=${status}`),
};

// Reports
export const reportsAPI = {
  dashboard: () => api.get('/reports/dashboard'),
  sales: (start_date, end_date) => api.get(`/reports/sales?start_date=${start_date}&end_date=${end_date}`),
  barbers: (start_date, end_date) => api.get(`/reports/barbers?start_date=${start_date}&end_date=${end_date}`),
  todayActivity: () => api.get('/reports/today/activity'),
  openSales: () => api.get('/reports/sales/open'),
  customers: (start_date, end_date) => api.get(`/reports/customers?start_date=${start_date}&end_date=${end_date}`),
  rankings: (start_date, end_date) => api.get(`/reports/rankings?start_date=${start_date}&end_date=${end_date}`),
  cashDetailed: (start_date, end_date) => api.get(`/reports/cash-detailed?start_date=${start_date}&end_date=${end_date}`),
  referrals: (start_date, end_date) => api.get(`/reports/referrals?start_date=${start_date}&end_date=${end_date}`),
  insights: (type, start_date, end_date, order = 'desc', limit = 10) =>
    api.get(`/reports/insights?type=${type}&start_date=${start_date}&end_date=${end_date}&order=${order}&limit=${limit}`),
  // Legacy aliases used across pages
  getDashboard: () => api.get('/reports/dashboard'),
  getSales: (startDate, endDate) => api.get(`/reports/sales?start_date=${startDate}&end_date=${endDate}`),
  getBarbers: (startDate, endDate) => api.get(`/reports/barbers?start_date=${startDate}&end_date=${endDate}`),
  getOpenSales: () => api.get('/reports/sales/open'),
  getTodayActivity: () => api.get('/reports/today/activity'),
  getCustomers: (startDate, endDate) => api.get(`/reports/customers?start_date=${startDate}&end_date=${endDate}`),
  getRankings: (startDate, endDate) => api.get(`/reports/rankings?start_date=${startDate}&end_date=${endDate}`),
  getReferrals: (startDate, endDate) => api.get(`/reports/referrals?start_date=${startDate}&end_date=${endDate}`),
  getEmployeePerformance: (startDate, endDate, barberId) => {
    const base = `/reports/employee-performance?start_date=${startDate}&end_date=${endDate}`;
    return api.get(barberId ? `${base}&barber_id=${barberId}` : base);
  },
  getTopClients: (startDate, endDate, limit = 10) =>
    api.get(`/reports/top-clients?start_date=${startDate}&end_date=${endDate}&limit=${limit}`),
};

export const cashCutsAPI = {
  list: (limit = 50) => api.get(`/cash-cuts?limit=${limit}`),
  close: (notes) => api.post('/cash-cuts/close', { notes }),
};

export default api;
